/* eslint-disable no-param-reassign */
import React, { useEffect, useRef } from 'react';
import {
  $createParagraphNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  BLUR_COMMAND,
  CLEAR_HISTORY_COMMAND,
  COMMAND_PRIORITY_LOW,
  HISTORY_PUSH_TAG,
  LexicalNode,
  SELECTION_CHANGE_COMMAND,
  type BaseSelection,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { HistoryState } from '@lexical/react/LexicalHistoryPlugin';
import { v4 as uuid } from 'uuid';
import { IInlineBox, InlineBoxType } from '$/components/InlineBox/types';
import type { MarkResult } from '$/components/InlineBox/types';
import {
  INLINE_BOX_BULK_REPLACE_TAG,
  buildEditorFromContent,
  getEditorTextLength,
  getTextNodeCaretStartOffset,
  insertTextAtSelection,
  normalizeMentionTextAnchors,
} from './editor';
import { resetHistoryState } from './history';
import {
  $createInlineBoxNode, $isInlineBoxNode, InlineBoxNode, INLINE_BOX_NODE_TYPE,
} from './nodes';
import { collectNodes } from './pluginUtils';
import {
  EditorSerializedContent, IInputBridge, MarkInsertOptions, SetValueOptions,
} from './types';
import type { MediaLimitConfig, InsertRejectedReason } from './index';
import { clearInlineBoxRuntimeState, setInlineBoxLoading } from './inlineBoxStateStore';
import { isPostTaskInlineBoxData } from './postTaskLabel';

/** 遍历当前编辑器 InlineBoxNode，按 mediaType 计数 */
function countInlineBoxes(root: ReturnType<typeof $getRoot>): { image: number; video: number; document: number; } {
  const counts = { image: 0, video: 0, document: 0 };
  const nodes: LexicalNode[] = [];
  root.getChildren().forEach((child) => collectNodes(child, nodes));
  nodes.forEach((node) => {
    if ($isInlineBoxNode(node)) {
      const { type } = (node as InlineBoxNode).__inlineBox;
      if (type === InlineBoxType.Image) counts.image += 1;
      else if (type === InlineBoxType.Video) counts.video += 1;
      else if (type === InlineBoxType.File) counts.document += 1;
    }
  });
  return counts;
}

/** 向外暴露输入框桥接能力时，统一把行内原子节点后的光标落到零宽锚点之后，规避 Safari caret 贴边错位。 */
export function InputBridgePlugin({
  disabledRef,
  maxLength,
  scrollCaretIntoView,
  historyState,
  syncSignature,
  onProgrammaticEdit,
  registerInputBridge,
  afterInsertInlineNode,
  mediaLimit,
  onInsertRejected,
  fetchMarkResults,
  markResultsMapRef,
  notifyMarkResultsChange,
}: {
  disabledRef: React.MutableRefObject<boolean>;
  maxLength?: number;
  scrollCaretIntoView: () => void;
  historyState: HistoryState;
  syncSignature: (content: EditorSerializedContent) => void;
  onProgrammaticEdit?: () => void;
  registerInputBridge: (bridge: IInputBridge | null) => void;
  afterInsertInlineNode?: (params: { inlineBox: IInlineBox; nodeKey: string; }) => Promise<void>;
  mediaLimit?: MediaLimitConfig;
  onInsertRejected?: (reason: InsertRejectedReason) => void;
  fetchMarkResults?: (params: { inlineBox: IInlineBox; nodeKey: string; }) => Promise<MarkResult[]>;
  markResultsMapRef: React.MutableRefObject<Map<string, MarkResult[]>>;
  notifyMarkResultsChange: () => void;
}) {
  const [editor] = useLexicalComposerContext();
  const maxLengthRef = useRef(maxLength);
  maxLengthRef.current = maxLength;
  const afterInsertInlineNodeRef = useRef(afterInsertInlineNode);
  afterInsertInlineNodeRef.current = afterInsertInlineNode;
  const mediaLimitRef = useRef(mediaLimit);
  mediaLimitRef.current = mediaLimit;
  const onInsertRejectedRef = useRef(onInsertRejected);
  onInsertRejectedRef.current = onInsertRejected;
  const syncSignatureRef = useRef(syncSignature);
  syncSignatureRef.current = syncSignature;
  const fetchMarkResultsRef = useRef(fetchMarkResults);
  fetchMarkResultsRef.current = fetchMarkResults;
  const lastSelectionRef = useRef<BaseSelection | null>(null);

  useEffect(() => {
    /** 缓存最近一次真实选区，覆盖“点击 inlineBox 后弹框插入”和“只移动光标不改内容”的场景。 */
    const cacheCurrentSelection = () => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || $isNodeSelection(selection)) {
        lastSelectionRef.current = selection.clone();
      }
    };

    const removeSelectionCacheListener = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        cacheCurrentSelection();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    const removeBlurSelectionCacheListener = editor.registerCommand(
      BLUR_COMMAND,
      () => {
        cacheCurrentSelection();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );

    /** 在调用插入命令的同步时刻读取当前选区，覆盖外部 mousedown 早于 blur 的调用时序。 */
    const getCurrentSelectionSnapshot = () => {
      let selectionSnapshot: BaseSelection | null = null;
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || $isNodeSelection(selection)) {
          selectionSnapshot = selection.clone();
        }
      });
      return selectionSnapshot;
    };

    const removeInlineBoxMutationListener = editor.registerMutationListener(
      InlineBoxNode,
      (mutatedNodes, { prevEditorState, updateTags }) => {
        if (updateTags?.has(INLINE_BOX_BULK_REPLACE_TAG)) {
          // 批量重写也需清理外置运行态，避免被移除节点的 selected/loading 残留
          mutatedNodes.forEach((mutation, nodeKey) => {
            if (mutation === 'destroyed') {
              clearInlineBoxRuntimeState(editor, nodeKey);
              markResultsMapRef.current.delete(nodeKey);
            }
          });
          return;
        }
        mutatedNodes.forEach((mutation, nodeKey) => {
          if (mutation !== 'destroyed') return;
          clearInlineBoxRuntimeState(editor, nodeKey);
          markResultsMapRef.current.delete(nodeKey);
          prevEditorState.read(() => {
            const node = $getNodeByKey(nodeKey);
            if ($isInlineBoxNode(node)) {
              node.__onRemove?.();
            }
          });
        });
      },
    );

    if (historyState.current === null) {
      resetHistoryState(historyState, editor);
    }

    /** 清空 Lexical HistoryPlugin 栈，并把当前 EditorState 设为新的撤销基线。 */
    const resetHistoryToCurrentState = () => {
      editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
      resetHistoryState(historyState, editor);
    };

    const queuedInlineBoxes: Array<{ inlineBox: IInlineBox; options?: MarkInsertOptions; }> = [];
    let queuedInsertFrameId: number | null = null;
    let shouldFocusQueuedBatch = false;
    let queuedBatchSelection: BaseSelection | null = null;
    let disposed = false;

    /** 外部按钮点击会让编辑器先失焦，插入前需恢复失焦前的 Lexical 选区。 */
    const restoreSelectionIfAttached = (selection: BaseSelection | null) => {
      if (!selection) return false;

      try {
        const nextSelection = selection.clone();
        const points = nextSelection.getStartEndPoints();
        if (points) {
          const [anchor, focus] = points;
          if (!$getNodeByKey(anchor.key) || !$getNodeByKey(focus.key)) {
            return false;
          }
        }
        $setSelection(nextSelection);
        return true;
      } catch {
        return false;
      }
    };

    const hasEditorFocus = () => {
      const rootElement = editor.getRootElement();
      return rootElement
        ? rootElement === document.activeElement || rootElement.contains(document.activeElement)
        : false;
    };

    const resetQueuedInlineInserts = () => {
      queuedInlineBoxes.length = 0;
      shouldFocusQueuedBatch = false;
      queuedBatchSelection = null;
      if (queuedInsertFrameId !== null) {
        window.cancelAnimationFrame(queuedInsertFrameId);
        queuedInsertFrameId = null;
      }
    };

    /** 将光标稳定放到 inlineBox 后方，纯 ZWS 锚点用父节点 offset，避免浏览器把 caret 映射到段落开头。 */
    const placeCaretAfterInlineBox = (node: InlineBoxNode) => {
      normalizeMentionTextAnchors();

      const nextSibling = node.getNextSibling();
      if ($isTextNode(nextSibling)) {
        const startOffset = getTextNodeCaretStartOffset(nextSibling);
        if (startOffset >= nextSibling.getTextContentSize()) {
          const parent = nextSibling.getParent();
          if ($isElementNode(parent)) {
            parent.select(nextSibling.getIndexWithinParent() + 1, nextSibling.getIndexWithinParent() + 1);
            return;
          }
        }
        nextSibling.select(startOffset, startOffset);
        return;
      }

      const parent = node.getParent();
      if ($isElementNode(parent)) {
        const offset = node.getIndexWithinParent() + 1;
        parent.select(offset, offset);
      }
    };

    /** 读取当前 NodeSelection 中被激活的 inlineBox 节点，用于替换和媒体数量校验。 */
    const getSelectedInlineBoxNode = () => {
      const selection = $getSelection();
      if (!$isNodeSelection(selection)) return null;
      const inlineNode = selection.getNodes().find((item) => $isInlineBoxNode(item));
      return inlineNode ? inlineNode as InlineBoxNode : null;
    };

    /** 后链路能力为单选标签，返回当前编辑器中已有的唯一 post_task 节点。 */
    const getPostTaskInlineBoxNode = () => {
      const nodes: LexicalNode[] = [];
      $getRoot().getChildren().forEach((child) => collectNodes(child, nodes));
      const postTaskNode = nodes.find((item) => (
        $isInlineBoxNode(item)
        && isPostTaskInlineBoxData(item.__inlineBox.data)
      ));
      return postTaskNode ? postTaskNode as InlineBoxNode : null;
    };

    /** 替换选中媒体时先扣除旧节点计数，避免到达上限后无法替换同类素材。 */
    const excludeReplacingInlineBoxFromCounts = (
      counts: ReturnType<typeof countInlineBoxes>,
      selectedInlineNode: InlineBoxNode | null,
    ) => {
      if (!selectedInlineNode) return counts;
      const nextCounts = { ...counts };
      const { type } = selectedInlineNode.__inlineBox;
      if (type === InlineBoxType.Image) {
        nextCounts.image = Math.max(0, nextCounts.image - 1);
      } else if (type === InlineBoxType.Video) {
        nextCounts.video = Math.max(0, nextCounts.video - 1);
      } else if (type === InlineBoxType.File) {
        nextCounts.document = Math.max(0, nextCounts.document - 1);
      }
      return nextCounts;
    };

    /** 在当前选区插入 inlineBox；NodeBottomMenu 场景可选择替换已激活的 inlineBox。 */
    const insertInlineBoxAtSelection = (inlineBox: IInlineBox, options?: MarkInsertOptions): string => {
      const node = $createInlineBoxNode(inlineBox);
      if (inlineBox.type === InlineBoxType.Mark) {
        // loading 默认 true，宿主可通过 options.loading 控制
        const initialLoading = options?.loading !== false;
        if (initialLoading) {
          setInlineBoxLoading(editor, node.getKey(), true);
        }
        // showOrder 写入 data，后续由 InlineBox 组件读取
        if (options?.showOrder === false) {
          const writable = node.getWritable() as InlineBoxNode;
          writable.__inlineBox = {
            ...writable.__inlineBox,
            data: { ...writable.__inlineBox.data, showOrder: false },
          };
        }
      }
      if (isPostTaskInlineBoxData(inlineBox.data)) {
        const existingPostTaskNode = getPostTaskInlineBoxNode();
        if (existingPostTaskNode) {
          const selectionBeforeReplacement = $getSelection();
          const selectionSnapshot = ($isRangeSelection(selectionBeforeReplacement)
            || $isNodeSelection(selectionBeforeReplacement))
            ? selectionBeforeReplacement.clone()
            : null;
          existingPostTaskNode.replace(node);
          normalizeMentionTextAnchors();
          if (!restoreSelectionIfAttached(selectionSnapshot)) {
            placeCaretAfterInlineBox(node);
          }
          return node.getKey();
        }
      }
      const selection = $getSelection();
      if ($isNodeSelection(selection)) {
        const anchorNode = getSelectedInlineBoxNode();
        if (anchorNode) {
          if (options?.replaceSelectedInlineBox) {
            anchorNode.replace(node);
            placeCaretAfterInlineBox(node);
            return node.getKey();
          }
          anchorNode.insertAfter(node);
          placeCaretAfterInlineBox(node);
          return node.getKey();
        }
      }
      if (selection) {
        if ($isRangeSelection(selection) && selection.isCollapsed()) {
          const anchorNode = selection.anchor.getNode();
          const anchorOffset = selection.anchor.offset;
          if ($isTextNode(anchorNode)) {
            const textSize = anchorNode.getTextContentSize();
            if (anchorOffset <= 0) {
              anchorNode.insertBefore(node);
              placeCaretAfterInlineBox(node);
              return node.getKey();
            }
            if (anchorOffset >= textSize) {
              anchorNode.insertAfter(node);
              placeCaretAfterInlineBox(node);
              return node.getKey();
            }

            // 文本中间插入 DecoratorNode 时显式拆分左右文本，避免 Lexical 默认 insertNodes 右偏一位。
            const splitNodes = anchorNode.splitText(anchorOffset);
            const leftNode = splitNodes[0];
            if ($isTextNode(leftNode)) {
              leftNode.insertAfter(node);
              placeCaretAfterInlineBox(node);
              return node.getKey();
            }
          }
        }

        selection.insertNodes([node]);

        // Lexical 在空段落中 insertNodes(DecoratorNode) 后会自动追加一个新的空段落，
        // 导致编辑器末尾多一个换行。如果节点所在段落之后紧跟一个空段落，且它是 root 的最后一个子节点，则移除。
        const parentParagraph = node.getParent();
        if (parentParagraph) {
          const nextSibling = parentParagraph.getNextSibling();
          if (
            nextSibling
            && $isElementNode(nextSibling)
            && nextSibling.getType() === 'paragraph'
            && nextSibling.getTextContentSize() === 0
            && nextSibling.getChildrenSize() === 0
            && nextSibling === $getRoot().getLastChild()
          ) {
            nextSibling.remove();
          }
        }

        placeCaretAfterInlineBox(node);
        return node.getKey();
      }
      const root = $getRoot();
      const paragraph = $createParagraphNode();
      paragraph.append(node);
      root.append(paragraph);
      placeCaretAfterInlineBox(node);
      return node.getKey();
    };

    /** 按联想触发词原位置恢复插入点，避免异步插入队列读取到点击弹框后的当前光标。 */
    const restoreInlineInsertAnchor = (options?: MarkInsertOptions): boolean => {
      const insertAnchor = options?.insertAnchor;
      if (!insertAnchor) return false;

      const node = $getNodeByKey(insertAnchor.nodeKey);
      if (!$isTextNode(node)) return false;

      const nextOffset = Math.min(insertAnchor.offset, node.getTextContentSize());
      node.select(nextOffset, nextOffset);
      return true;
    };

    const prepareInlineBox = (inlineBox: IInlineBox): IInlineBox => ({
      ...inlineBox,
      data: {
        ...inlineBox.data,
        index: inlineBox.data.index || uuid(),
      },
    });

    const insertSingleInlineNode = (
      inlineBox: IInlineBox,
      options?: MarkInsertOptions,
      selectionToRestore?: BaseSelection | null,
    ): boolean => {
      if (disabledRef.current) return false;

      const nextInlineBox = prepareInlineBox(inlineBox);
      const isMediaType = nextInlineBox.type === InlineBoxType.Image
        || nextInlineBox.type === InlineBoxType.Video
        || nextInlineBox.type === InlineBoxType.File;

      let rejected = false;
      let insertedNodeKey = '';

      editor.update(() => {
        const restoredAnchor = restoreInlineInsertAnchor(options);
        const restoredSelection = restoredAnchor || restoreSelectionIfAttached(selectionToRestore || null);
        if (selectionToRestore && !restoredSelection) {
          // 空输入框仅有触发符时，点击弹框会先删除该 TextNode；失效选区回落到当前段落末尾。
          const lastChild = $getRoot().getLastChild();
          if ($isElementNode(lastChild)) {
            lastChild.selectEnd();
          } else {
            $setSelection(null);
          }
        }

        if (isMediaType && mediaLimitRef.current) {
          const selectedInlineNode = options?.replaceSelectedInlineBox ? getSelectedInlineBoxNode() : null;
          const counts = excludeReplacingInlineBoxFromCounts(countInlineBoxes($getRoot()), selectedInlineNode);
          const rejection = mediaLimitRef.current.validate(nextInlineBox, counts);
          if (rejection) {
            onInsertRejectedRef.current?.(rejection);
            rejected = true;
            return;
          }
        }

        insertedNodeKey = insertInlineBoxAtSelection(nextInlineBox, options);
      }, {
        discrete: true,
        tag: HISTORY_PUSH_TAG,
      });

      if (!rejected) {
        onProgrammaticEdit?.();
        afterInsertInlineNodeRef.current?.({ inlineBox: nextInlineBox, nodeKey: insertedNodeKey });

        if (nextInlineBox.type === InlineBoxType.Mark) {
          // 同步模式：宿主直接传入 resultList，立即存储并关闭 loading
          if (options?.resultList) {
            markResultsMapRef.current.set(insertedNodeKey, options.resultList);
            setInlineBoxLoading(editor, insertedNodeKey, false);
            notifyMarkResultsChange();
          } else if (fetchMarkResultsRef.current) {
            // 数据驱动模式：自动拉取 Mark 识别结果
            const fetchFn = fetchMarkResultsRef.current;
            fetchFn({ inlineBox: nextInlineBox, nodeKey: insertedNodeKey })
              .then((results) => {
                if (disposed) return;
                markResultsMapRef.current.set(insertedNodeKey, results);
                setInlineBoxLoading(editor, insertedNodeKey, false);
                notifyMarkResultsChange();
              })
              .catch(() => {
                if (disposed) return;
                setInlineBoxLoading(editor, insertedNodeKey, false);
              });
          }
        }
      }

      return !rejected;
    };

    const flushQueuedInlineInsert = () => {
      if (disposed) return;

      const nextItem = queuedInlineBoxes.shift();
      if (!nextItem) {
        queuedInsertFrameId = null;
        shouldFocusQueuedBatch = false;
        return;
      }

      if (shouldFocusQueuedBatch) {
        editor.focus();
        shouldFocusQueuedBatch = false;
      }

      const selectionToRestore = queuedBatchSelection;
      queuedBatchSelection = null;
      const success = insertSingleInlineNode(nextItem.inlineBox, nextItem.options, selectionToRestore);
      if (success) {
        queueMicrotask(() => {
          if (disposed) return;
          editor.focus();
          scrollCaretIntoView();
        });
      }

      if (!queuedInlineBoxes.length) {
        queuedInsertFrameId = null;
        return;
      }

      queuedInsertFrameId = window.requestAnimationFrame(() => {
        queuedInsertFrameId = null;
        flushQueuedInlineInsert();
      });
    };

    const enqueueInlineNodeInsertion = (inlineBox: IInlineBox, options?: MarkInsertOptions): boolean => {
      if (disabledRef.current) return false;

      if (!queuedInlineBoxes.length && queuedInsertFrameId === null) {
        shouldFocusQueuedBatch = !hasEditorFocus();
        queuedBatchSelection = getCurrentSelectionSnapshot()
          || lastSelectionRef.current?.clone()
          || null;
      }

      queuedInlineBoxes.push({ inlineBox, options });
      if (queuedInsertFrameId === null) {
        queuedInsertFrameId = window.requestAnimationFrame(() => {
          queuedInsertFrameId = null;
          flushQueuedInlineInsert();
        });
      }

      return true;
    };

    registerInputBridge({
      focus: () => editor.focus(),
      clear: () => {
        resetQueuedInlineInserts();
        lastSelectionRef.current = null;
        markResultsMapRef.current.clear();
        onProgrammaticEdit?.();
        editor.focus();
        editor.update(() => {
          buildEditorFromContent([]);
        }, { tag: INLINE_BOX_BULK_REPLACE_TAG, discrete: true });
        resetHistoryToCurrentState();
        notifyMarkResultsChange();
      },
      /** 外部回填不聚焦时不接管选区和历史快捷键，避免抢走画布撤销。 */
      setValue: (content: EditorSerializedContent, options?: SetValueOptions) => {
        const shouldFocus = options?.focus ?? false;
        const shouldSelectEnd = options?.selectEnd ?? false;
        resetQueuedInlineInserts();
        lastSelectionRef.current = null;
        markResultsMapRef.current.clear();
        let restoredResults: Map<string, MarkResult[]> | undefined;
        if (shouldFocus) {
          onProgrammaticEdit?.();
          editor.focus();
        }
        editor.update(() => {
          restoredResults = buildEditorFromContent(content || [], { selectEnd: shouldFocus && shouldSelectEnd });
        }, { tag: INLINE_BOX_BULK_REPLACE_TAG, discrete: true });
        if (restoredResults?.size) {
          restoredResults.forEach((results, key) => {
            markResultsMapRef.current.set(key, results);
          });
        }
        resetHistoryToCurrentState();
        syncSignatureRef.current(content || []);
        notifyMarkResultsChange();
        if (shouldFocus) {
          queueMicrotask(() => {
            scrollCaretIntoView();
          });
        }
      },
      resetHistoryStack: () => {
        resetHistoryToCurrentState();
      },
      insertInlineNode: (inlineBox: IInlineBox, options?: MarkInsertOptions): boolean => enqueueInlineNodeInsertion(inlineBox, options),
      insertText: (text: string) => {
        if (disabledRef.current) return;
        onProgrammaticEdit?.();
        editor.focus();
        editor.update(() => {
          let nextText = text;
          if (maxLengthRef.current) {
            const length = getEditorTextLength(editor.getEditorState());
            const remain = maxLengthRef.current - length;
            if (remain <= 0) return;
            if (nextText.length > remain) nextText = nextText.slice(0, remain);
          }

          if (!insertTextAtSelection(nextText)) {
            const root = $getRoot();
            const paragraph = $createParagraphNode();
            root.append(paragraph);
            paragraph.selectEnd();
            insertTextAtSelection(nextText);
          }
        });
        queueMicrotask(() => {
          scrollCaretIntoView();
        });
      },
      updateMarkData: (nodeKey: string, patch: Partial<IInlineBox['data']>) => {
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if (!node || node.getType() !== INLINE_BOX_NODE_TYPE) return;
          const writable = node.getWritable() as InlineBoxNode;
          writable.__inlineBox = {
            ...writable.__inlineBox,
            data: { ...writable.__inlineBox.data, ...patch },
          };
        });
      },
      setMarkLoading: (nodeKey: string, loading: boolean) => {
        setInlineBoxLoading(editor, nodeKey, loading);
      },
      getMarkResults: (nodeKey: string) => markResultsMapRef.current.get(nodeKey),
    });

    return () => {
      disposed = true;
      resetQueuedInlineInserts();
      removeSelectionCacheListener();
      removeBlurSelectionCacheListener();
      removeInlineBoxMutationListener();
      registerInputBridge(null);
    };
  }, [editor, disabledRef, scrollCaretIntoView, historyState, onProgrammaticEdit, registerInputBridge, markResultsMapRef, notifyMarkResultsChange]);

  return null;
}
